import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Send, FileText, Scale, Shield, Lock, ChevronRight, Clock,
    CheckCircle2, AlertTriangle, PlusCircle, Users, Gavel, MessageSquare,
    Sparkles, XCircle,
} from "lucide-react";
import api, { resolveAvatar } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const WATERMARK = "DRAFT — PENDING ATTORNEY REVIEW";

const DOC_STATUS_TONE = {
    draft:                    "bg-slate-100 text-slate-700  border-slate-200",
    pending_attorney_review:  "bg-amber-50  text-amber-800  border-amber-200",
    attorney_approved:        "bg-emerald-50 text-emerald-700 border-emerald-200",
    filed:                    "bg-blue-50   text-blue-700   border-blue-200",
    served:                   "bg-blue-50   text-blue-700   border-blue-200",
    withdrawn:                "bg-slate-100 text-slate-500  border-slate-200",
};

const DOC_STATUS_LABEL = {
    draft: "Draft",
    pending_attorney_review: "Pending review",
    attorney_approved: "Approved",
    filed: "Filed",
    served: "Served",
    withdrawn: "Withdrawn",
};

const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

export default function LegalMatterWorkspace() {
    const { matterId } = useParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [matter, setMatter] = useState(null);
    const [docs, setDocs] = useState([]);
    const [events, setEvents] = useState([]);
    const [deadlines, setDeadlines] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [auditAccess, setAuditAccess] = useState(true);
    const [legalHired, setLegalHired] = useState([]);
    const [selectedWorker, setSelectedWorker] = useState("");
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const scrollRef = useRef(null);

    const isAttorney = useMemo(() => {
        if (!user || !matter) return false;
        if (user.role === "platform_owner") return true;
        if (user.role === "attorney") return true;
        return (matter.attorneys_of_record || []).some((a) => a.user_id === user.id);
    }, [user, matter]);

    const loadAll = async () => {
        try {
            const [m, dList, tList, dlList, hired] = await Promise.all([
                api.get(`/legal/matters/${matterId}`),
                api.get(`/legal/matters/${matterId}/documents`),
                api.get(`/legal/matters/${matterId}/timeline`),
                api.get(`/legal/matters/${matterId}/deadlines`),
                api.get("/workers/hired"),
            ]);
            setMatter(m.data);
            setDocs(d => (dList.data.documents || []));
            setEvents(tList.data.events || []);
            setDeadlines(dlList.data.deadlines || []);
            const hiredLegal = (hired.data.hired || []).filter(
                (h) => (h.worker?.department || "").toLowerCase() === "legal"
            );
            setLegalHired(hiredLegal);
            if (!selectedWorker && hiredLegal[0]) setSelectedWorker(hiredLegal[0].id);
            // Audit log (may 403 for non-attorneys)
            try {
                const al = await api.get(`/legal/matters/${matterId}/audit-log`);
                setAuditLog(al.data.log || []);
                setAuditAccess(true);
            } catch (err) {
                if (err.response?.status === 403) setAuditAccess(false);
            }
            setError("");
        } catch (e) {
            const detail = e.response?.data?.detail;
            const errCode = detail?.error;
            if (errCode === "ethical_wall_blocked") {
                setError("You are excluded from this matter by an ethical wall.");
            } else {
                setError(detail?.message || detail || "Failed to load matter.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matterId]);

    useEffect(() => {
        // Auto-scroll conversation feed
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [docs, events]);

    const sendMessage = async () => {
        const content = msg.trim();
        if (!content) return;
        if (!selectedWorker) {
            toast.error("Hire a legal Workforce Member first (Workforce → Legal).");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/legal/matters/${matterId}/ai-message`, {
                hired_worker_id: selectedWorker,
                content,
            });
            setMsg("");
            toast.success("Draft added to the matter.");
            // Merge new doc + event locally so UI is instant, then refetch
            setDocs((d) => [data.document, ...d]);
            loadAll();
        } catch (e) {
            const d = e.response?.data?.detail;
            const errCode = d?.error;
            if (errCode === "ethical_wall_blocked") toast.error(d.message);
            else toast.error(typeof d === "string" ? d : (d?.message || "AI request failed."));
        } finally {
            setSending(false);
        }
    };

    const approveDoc = async (docId) => {
        if (!isAttorney) {
            toast.error('Attorney authorization required. Only users with role "attorney" or listed as an attorney of record on this matter can approve.');
            return;
        }
        try {
            await api.post(`/legal/matters/${matterId}/documents/${docId}/approve`);
            toast.success("Document approved.");
            loadAll();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : (d?.message || "Approval failed."));
        }
    };

    /* ---------- Loading / error states ---------- */
    if (loading) {
        return (
            <div className="h-full grid place-items-center text-sm text-gray-500" data-testid="matter-loading">
                Loading matter…
            </div>
        );
    }
    if (error) {
        return (
            <div className="h-full grid place-items-center p-8" data-testid="matter-error">
                <div className="max-w-md text-center">
                    <XCircle className="w-8 h-8 text-rose-500 mx-auto" strokeWidth={1.75} />
                    <div className="mt-3 text-lg font-medium text-gray-900">{error}</div>
                    <Link to="/app/legal/matters" className="mt-4 inline-flex items-center gap-1 text-[13px] text-[#16a34a] hover:text-[#15803d]">
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to matters
                    </Link>
                </div>
            </div>
        );
    }
    if (!matter) return null;

    return (
        <div className="h-full flex overflow-hidden" data-testid="matter-workspace">
            {/* ---------- LEFT RAIL ---------- */}
            <aside className="w-[300px] flex-shrink-0 border-r border-[#e8e8e3] bg-white overflow-y-auto wfos-scrollbar">
                <div className="p-5">
                    <Link
                        to="/app/legal/matters"
                        className="inline-flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-900"
                        data-testid="matter-back-link"
                    >
                        <ArrowLeft className="w-3 h-3" strokeWidth={2} /> All matters
                    </Link>
                    <div className="mt-4 flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-[#dcfce7] grid place-items-center">
                            <Scale className="w-5 h-5 text-[#16a34a]" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-[11px] font-mono text-gray-500 tabular-nums">{matter.matter_number}</div>
                            <div className="text-[10.5px] font-semibold uppercase tracking-widest text-gray-500">
                                {(matter.matter_type || "").replace(/_/g, " ")}
                            </div>
                        </div>
                    </div>
                    <h2 className="mt-3 text-[16.5px] font-semibold text-gray-900 leading-tight">{matter.title}</h2>

                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10.5px] font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" /> {matter.status}
                        </span>
                        {matter.legal_hold && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-[10.5px] font-medium">
                                <Lock className="w-2.5 h-2.5" strokeWidth={2.5} /> Legal hold
                            </span>
                        )}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-[10.5px] font-medium">
                            {(matter.privilege_default || "attorney_client").replace(/_/g, "-")}
                        </span>
                    </div>

                    {matter.description && (
                        <p className="mt-3 text-[12.5px] text-gray-600 leading-relaxed">{matter.description}</p>
                    )}

                    <SectionHeader label="Meta" />
                    <MetaRow label="Jurisdiction" value={matter.jurisdiction || "—"} />
                    <MetaRow label="Court" value={matter.court || "—"} />
                    <MetaRow label="Opposing counsel" value={matter.opposing_counsel || "—"} />
                    <MetaRow label="Opened" value={new Date(matter.created_at).toLocaleDateString()} />

                    <SectionHeader label="Attorneys of record" />
                    {(matter.attorneys_of_record || []).length === 0 ? (
                        <div className="text-[12px] text-gray-400 italic">None assigned yet.</div>
                    ) : (
                        <ul className="space-y-1">
                            {matter.attorneys_of_record.map((a, i) => (
                                <li key={i} className="text-[12.5px] text-gray-700 flex items-center gap-1.5">
                                    <Gavel className="w-3 h-3 text-[#16a34a]" strokeWidth={2} />
                                    <span>{a.name || a.user_id}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <SectionHeader label="AI legal team" />
                    {(legalHired || []).length === 0 ? (
                        <div className="text-[12px] text-gray-400 italic">
                            No legal AI hired yet.{" "}
                            <Link to="/app/workforce" className="text-[#16a34a] hover:text-[#15803d]">Hire one →</Link>
                        </div>
                    ) : (
                        <ul className="space-y-1.5" data-testid="matter-ai-team">
                            {legalHired.map((h) => (
                                <li key={h.id} className="flex items-center gap-2">
                                    <img
                                        src={resolveAvatar(h.worker?.avatar_url)}
                                        alt={h.worker?.name}
                                        className="w-6 h-6 rounded-full object-cover border border-[#e8e8e3]"
                                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                    />
                                    <div className="min-w-0 flex-1 leading-tight">
                                        <div className="text-[12.5px] text-gray-900 truncate">{h.worker?.name}</div>
                                        <div className="text-[10.5px] text-gray-500 truncate">{h.worker?.role}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <SectionHeader label="Ethical wall (deny-list)" />
                    <div className="text-[12px] text-gray-600">
                        {(matter.ethical_wall_users || []).length === 0
                            ? "No users excluded — everyone in the tenant has access."
                            : `${matter.ethical_wall_users.length} user(s) excluded from this matter.`}
                    </div>

                    <SectionHeader label="Attorney supervision" />
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] leading-relaxed text-amber-900">
                        Every AI-generated output is marked <b>DRAFT — PENDING ATTORNEY REVIEW</b>.
                        Only users with role <code>attorney</code> or listed as an attorney of record
                        can approve documents.
                    </div>
                </div>
            </aside>

            {/* ---------- CENTER: docs & events ---------- */}
            <main className="flex-1 min-w-0 flex flex-col bg-[#fafaf7]">
                <div ref={scrollRef} className="flex-1 overflow-y-auto wfos-scrollbar p-6 space-y-4">
                    {docs.length === 0 && events.length === 0 && (
                        <div className="wfos-card p-6 text-center max-w-lg mx-auto mt-6" data-testid="matter-empty-feed">
                            <Sparkles className="w-6 h-6 text-[#16a34a] mx-auto" strokeWidth={1.75} />
                            <div className="mt-2 text-[14.5px] font-semibold text-gray-900">
                                Start the matter workflow
                            </div>
                            <p className="mt-1 text-[13px] text-gray-600">
                                Ask a legal Workforce Member to draft a memo, research a question, or outline
                                a filing strategy. Their outputs will appear here as drafts pending
                                attorney review.
                            </p>
                        </div>
                    )}

                    {docs.map((d) => (
                        <DocCard
                            key={d.id}
                            doc={d}
                            isAttorney={isAttorney}
                            onApprove={() => approveDoc(d.id)}
                        />
                    ))}

                    {events.filter((e) => (e.linked_document_ids || []).length === 0).map((ev) => (
                        <EventRow key={ev.id} event={ev} />
                    ))}
                </div>

                {/* AI composer */}
                <div className="border-t border-[#e8e8e3] bg-white p-4">
                    <div className="max-w-3xl mx-auto flex items-center gap-2">
                        <select
                            value={selectedWorker}
                            onChange={(e) => setSelectedWorker(e.target.value)}
                            data-testid="matter-worker-select"
                            className="rounded-lg border border-[#e8e8e3] bg-white h-10 px-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] max-w-[220px]"
                        >
                            {legalHired.length === 0 && <option value="">No legal AI hired</option>}
                            {legalHired.map((h) => (
                                <option key={h.id} value={h.id}>
                                    {h.worker?.name} — {h.worker?.role}
                                </option>
                            ))}
                        </select>
                        <input
                            value={msg}
                            onChange={(e) => setMsg(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                            data-testid="matter-composer-input"
                            placeholder={legalHired.length ? "Ask your legal AI for a memo, research, or draft…" : "Hire a legal Workforce Member to start drafting."}
                            className="flex-1 rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            disabled={sending || legalHired.length === 0}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={sending || !msg.trim() || legalHired.length === 0}
                            data-testid="matter-composer-send"
                            className="inline-flex items-center gap-1 rounded-lg bg-[#16a34a] text-white h-10 px-3.5 text-sm font-medium hover:bg-[#15803d] disabled:opacity-60"
                        >
                            <Send className="w-4 h-4" strokeWidth={2} /> {sending ? "Sending…" : "Send"}
                        </button>
                    </div>
                    <div className="max-w-3xl mx-auto mt-2 text-[11px] text-gray-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" strokeWidth={2} />
                        AI outputs are drafts. Attorney review required before filing or service.
                    </div>
                </div>
            </main>

            {/* ---------- RIGHT RAIL: deadlines + audit ---------- */}
            <aside className="w-[340px] flex-shrink-0 border-l border-[#e8e8e3] bg-white overflow-y-auto wfos-scrollbar">
                <div className="p-5 space-y-5">
                    {/* Deadlines */}
                    <div>
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                Deadlines
                            </div>
                            <span className="text-[11px] text-gray-400">{deadlines.length}</span>
                        </div>
                        {deadlines.length === 0 ? (
                            <div className="mt-2 text-[12px] text-gray-400 italic">No deadlines yet.</div>
                        ) : (
                            <ul className="mt-3 space-y-2" data-testid="matter-deadlines">
                                {deadlines.map((d) => (
                                    <li key={d.id} className="rounded-lg border border-[#e8e8e3] bg-[#fafaf7] p-2.5">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-[12.5px] font-medium text-gray-900 truncate">{d.title}</div>
                                            {d.verified_at ? (
                                                <span className="wfos-pill wfos-pill--green text-[9.5px] px-1.5 py-0.5">
                                                    <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2.5} /> Verified
                                                </span>
                                            ) : (
                                                <span className="wfos-pill text-[9.5px] px-1.5 py-0.5">
                                                    <Clock className="w-2.5 h-2.5" strokeWidth={2} /> {d.status}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                                            <span>{new Date(d.date).toLocaleDateString()}</span>
                                            {d.ai_extracted && <span className="text-amber-600">AI-extracted</span>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Audit log */}
                    <div>
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                Audit log
                            </div>
                            {!auditAccess && (
                                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                    <Shield className="w-2.5 h-2.5" strokeWidth={2} /> attorneys only
                                </span>
                            )}
                        </div>
                        {!auditAccess ? (
                            <div className="mt-2 text-[12px] text-gray-400 italic">
                                Only attorneys of record and users with the <code>attorney</code> role can view the audit log.
                            </div>
                        ) : auditLog.length === 0 ? (
                            <div className="mt-2 text-[12px] text-gray-400 italic">No entries yet.</div>
                        ) : (
                            <ul className="mt-3 space-y-2.5 max-h-[520px] overflow-y-auto wfos-scrollbar pr-1" data-testid="matter-audit-log">
                                {auditLog.slice(0, 40).map((e) => (
                                    <li key={e.id} className="text-[12px] text-gray-700 leading-relaxed">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                            <span className="font-medium text-gray-900">{e.action_type}</span>
                                        </div>
                                        <div className="ml-3.5 text-[11px] text-gray-500">
                                            {fmt(e.timestamp)}
                                            {e.actor_ai_worker_slug ? ` · ${e.actor_ai_worker_slug}` : ""}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </aside>
        </div>
    );
}

/* ---------- Sub-components ---------- */
function SectionHeader({ label }) {
    return (
        <div className="mt-5 mb-2 text-[10.5px] font-semibold uppercase tracking-widest text-gray-500">
            {label}
        </div>
    );
}

function MetaRow({ label, value }) {
    return (
        <div className="flex items-baseline justify-between py-1 text-[12.5px] border-b border-[#f4f4ef] last:border-b-0">
            <span className="text-gray-500">{label}</span>
            <span className="text-gray-900 font-medium truncate max-w-[65%] text-right">{value}</span>
        </div>
    );
}

function DocCard({ doc, isAttorney, onApprove }) {
    const [expanded, setExpanded] = useState(false);
    const status = doc.status || "draft";
    return (
        <div
            className={`rounded-xl border p-4 bg-white ${status === "pending_attorney_review" ? "border-amber-200" : "border-[#e8e8e3]"}`}
            data-testid={`matter-doc-${doc.id}`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#16a34a] flex-shrink-0" strokeWidth={2} />
                    <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-gray-900 truncate">{doc.title}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                            {(doc.document_type || "document").replace(/_/g, " ")}
                            {doc.created_by_ai_worker_slug ? ` · by ${doc.created_by_ai_worker_slug}` : ""}
                            {" · "}{fmt(doc.created_at)}
                        </div>
                    </div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-medium ${DOC_STATUS_TONE[status] || ""}`}>
                    {DOC_STATUS_LABEL[status] || status}
                </span>
            </div>

            {/* Watermark banner — only for non-approved documents */}
            {status !== "attorney_approved" && status !== "filed" && status !== "served" && (
                <div
                    className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-900"
                    data-testid={`matter-doc-watermark-${doc.id}`}
                >
                    <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2.5} />
                    {WATERMARK}
                </div>
            )}

            <div className={`mt-3 whitespace-pre-wrap text-[13.5px] text-gray-800 leading-relaxed ${expanded ? "" : "line-clamp-6"}`}>
                {doc.content}
            </div>
            <div className="mt-2 flex items-center justify-between">
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="text-[12px] text-[#16a34a] hover:text-[#15803d] font-medium inline-flex items-center gap-1"
                    data-testid={`matter-doc-toggle-${doc.id}`}
                >
                    {expanded ? "Collapse" : "Read full draft"} <ChevronRight className={`w-3 h-3 ${expanded ? "rotate-90" : ""} transition-transform`} strokeWidth={2} />
                </button>
                {(status === "pending_attorney_review" || status === "draft") && (
                    <button
                        onClick={onApprove}
                        title={isAttorney ? "Approve this draft as attorney of record" : "Attorney role or attorney-of-record required — click to see details"}
                        data-testid={`matter-doc-approve-${doc.id}`}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 h-7 text-[11.5px] font-medium ${isAttorney ? "bg-[#16a34a] text-white hover:bg-[#15803d]" : "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"}`}
                    >
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                        {isAttorney ? "Approve as attorney" : "Attorney approval required"}
                    </button>
                )}
                {status === "attorney_approved" && (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700 font-medium">
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> Approved
                    </span>
                )}
            </div>
        </div>
    );
}

function EventRow({ event }) {
    return (
        <div className="flex items-start gap-3 pl-1" data-testid={`matter-event-${event.id}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2" />
            <div className="min-w-0">
                <div className="text-[12.5px] text-gray-900">{event.title}</div>
                <div className="text-[11px] text-gray-500">
                    {fmt(event.timestamp)}
                    {event.actor_ai_worker_slug ? ` · ${event.actor_ai_worker_slug}` : ""}
                </div>
            </div>
        </div>
    );
}
